import { Heart, Megaphone, PanelBottom, Search, ShoppingBag, SunMoon, type LucideIcon } from 'lucide-react';

import {
  COLOR_MODE_DEFAULTS,
  SEARCH_SUGGEST_STYLES,
  themePluginsSchema,
  type StorefrontTheme,
  type ThemePlugins,
} from '@/features/storefront/themeSchema';
import { themeQuickCart } from '@/previewThemes/registry';

/**
 * Plugins: shop-wide add-ons a merchant switches on whatever theme they wear.
 *
 * A plugin is not a section. A section has a place in one page's stack and
 * the merchant arranges it; a plugin is behaviour the whole shop takes on - a
 * drawer, a floating button - with nowhere on a page to put it. So plugins are
 * switched on in the customiser's Plugins panel, not added from the section
 * picker, and saved on the theme document (`theme.plugins`), which gives them
 * drafts, preview, publish and history like every other theme setting.
 *
 * Each plugin can follow its theme: a theme designed around one (Kinetic and
 * the quick cart) has it on until the merchant says otherwise, and every other
 * theme has it off. Adding a plugin means a field in `themePluginsSchema`, the
 * mirror in `backend/app/schemas/store.py`, an entry here, and its copy under
 * `themeEditor.plugins.<id>.*` and `tipsTheme.plugins.<id>.*`.
 */

export type PluginId = keyof ThemePlugins;

/**
 * One of a plugin's own settings, as the Plugins panel draws it. A switch
 * unless it says otherwise; `hint` means the copy has a `<field>_hint` line
 * under the control.
 */
export type PluginOption = { field: string; hint?: boolean } & (
  | { kind?: 'switch' }
  | { kind: 'text'; max: number; multiline?: boolean }
  | { kind: 'number'; min: number; max: number }
  /** A hex, or null for the theme's primary colour. */
  | { kind: 'color' }
  | { kind: 'image' }
  /** Which kinds of page the plugin shows on (`PROMO_POPUP_PAGES`). */
  | { kind: 'pages' }
  /** One of a few named values; each has its copy at `<field>_<choice>`. */
  | { kind: 'choice'; choices: readonly string[] }
);

export interface PluginSpec {
  id: PluginId;
  icon: LucideIcon;
  /** Whether the plugin is on for a theme whose merchant has not chosen. */
  themeDefault: (family: string) => boolean;
  /** The plugin's own settings, offered while it is on. */
  options: readonly PluginOption[];
}

export const PLUGINS: readonly PluginSpec[] = [
  {
    id: 'quick_cart',
    icon: ShoppingBag,
    themeDefault: themeQuickCart,
    options: [
      { field: 'open_on_add', hint: true },
      { field: 'free_shipping_bar', hint: true },
      { field: 'recommendations' },
    ],
  },
  {
    id: 'promo_popup',
    icon: Megaphone,
    // No theme is designed around a popup; it is the merchant's call alone.
    themeDefault: () => false,
    options: [
      { field: 'show_on', kind: 'pages' },
      { field: 'delay_seconds', kind: 'number', min: 0, max: 60, hint: true },
      { field: 'eyebrow', kind: 'text', max: 60 },
      { field: 'headline', kind: 'text', max: 120 },
      { field: 'subtitle', kind: 'text', max: 200, multiline: true },
      { field: 'button_label', kind: 'text', max: 30 },
      { field: 'image_url', kind: 'image' },
      { field: 'panel_color', kind: 'color', hint: true },
      { field: 'collect_name' },
    ],
  },
  {
    id: 'search_suggest',
    icon: Search,
    // No theme is drawn around it: the icon stays a plain link until asked.
    themeDefault: () => false,
    options: [
      { field: 'style', kind: 'choice', choices: SEARCH_SUGGEST_STYLES },
      { field: 'show_products' },
      { field: 'show_categories' },
      { field: 'max_results', kind: 'number', min: 3, max: 10, hint: true },
    ],
  },
  {
    id: 'wishlist',
    icon: Heart,
    // No theme is drawn around hearts: the merchant switches them on.
    themeDefault: () => false,
    options: [
      { field: 'show_in_header' },
      { field: 'show_on_cards' },
      { field: 'show_on_product' },
    ],
  },
  {
    id: 'color_mode',
    icon: SunMoon,
    // Every theme can wear it, none is drawn around it: the merchant's call.
    themeDefault: () => false,
    options: [
      { field: 'default_mode', kind: 'choice', choices: COLOR_MODE_DEFAULTS, hint: true },
      { field: 'show_toggle' },
      // The other mode's own colours; each one left empty is derived from
      // the theme's (`colorMode/palette.ts`).
      { field: 'alternate_primary', kind: 'color', hint: true },
      { field: 'alternate_background', kind: 'color' },
      { field: 'alternate_surface', kind: 'color' },
      { field: 'alternate_text', kind: 'color' },
      { field: 'alternate_muted', kind: 'color' },
    ],
  },
  {
    id: 'app_tab_bar',
    icon: PanelBottom,
    // An app's way around rather than any theme's: the merchant's call.
    themeDefault: () => false,
    options: [{ field: 'show_labels' }],
  },
];

const DEFAULT_PLUGINS: ThemePlugins = themePluginsSchema.parse({});

/** One plugin's settings, defaults filled in. */
export function themePlugin<K extends PluginId>(
  theme: Pick<StorefrontTheme, 'plugins'> | undefined,
  id: K,
): ThemePlugins[K] {
  return theme?.plugins ? themePluginsSchema.parse(theme.plugins)[id] : DEFAULT_PLUGINS[id];
}

/** Whether a plugin is on: the merchant's choice, or the theme's when they have none. */
export function pluginEnabled(
  theme: Pick<StorefrontTheme, 'plugins'> | undefined,
  id: PluginId,
  family: string,
): boolean {
  const spec = PLUGINS.find((plugin) => plugin.id === id);
  return themePlugin(theme, id).enabled ?? spec?.themeDefault(family) ?? false;
}
